import { useCallback, useRef } from 'react';
import snakeCase from 'lodash/snakeCase';
import { type FlagId, useFlagsService, toFlagId } from '@atlassian/jira-flags';
import { toShortFormItsmPractice } from '@atlassian/jira-servicedesk-work-category/src/controllers/to-work-category-short-form/index.tsx';
import { useRouterActions, usePathParam } from '@atlassian/react-resource-router';
import messages from './messages';

const useFlags = () => {
	const { showFlag, dismissFlag } = useFlagsService();
	const { push } = useRouterActions();
	const [projectKey = ''] = usePathParam('projectKey');
	const flagIdRef = useRef<{ [key: string]: FlagId }>({});

	return {
		showSuccessFlag: useCallback(
			(feature: string, enabled: boolean) => {
				const featureMessageKey = snakeCase(feature);
				const flagIdKey = `${feature}-service-feature-success`;
				const { current: flagIds } = flagIdRef;
				// toFlagId creates a new string reference. But we want to reuse
				// flag ids so the most relevant flag for a feature is shown
				flagIds[flagIdKey] = flagIds[flagIdKey] || toFlagId(flagIdKey);

				const id = flagIds[flagIdKey];
				const onClick = () => {
					dismissFlag(id);
				};
				// Dismiss previous flag with the same id. So user cannot
				// interact with out of update flags
				dismissFlag(id);

				showFlag({
					id,
					type: 'success',
					isAutoDismiss: !enabled,
					title: [messages.successTitle, { feature: featureMessageKey, value: enabled }],
					description: enabled
						? messages.successEnableDescription
						: messages.successDisableDescription,
					actions: enabled
						? [
								{
									content: messages.successEnableDismiss,
									onClick,
								},
								{
									content: messages.successEnableAction,
									onClick: () => {
										const practiceUrlFragment = `/category/${toShortFormItsmPractice(feature)}`;
										dismissFlag(id);
										push(
											`/jira/servicedesk/projects/${projectKey}/settings/request-types${practiceUrlFragment}`,
										);
									},
								},
							]
						: [{ content: messages.successDisableDismiss, onClick }],
				});
			},
			[dismissFlag, projectKey, push, showFlag],
		),
		showErrorFlag: useCallback(
			(feature: string, enabled: boolean) => {
				const featureMessageKey = snakeCase(feature);
				const flagIdKey = `${feature}-service-feature-error`;
				const { current: flagIds } = flagIdRef;
				flagIds[flagIdKey] = flagIds[flagIdKey] || toFlagId(flagIdKey);

				const id: FlagId = flagIds[flagIdKey];
				dismissFlag(id);

				showFlag({
					id,
					type: 'error',
					isAutoDismiss: true,
					title: [
						messages.errorTitle,
						{
							feature: featureMessageKey,
							value: enabled,
						},
					],
					description: messages.errorDesciption,
					actions: [
						{
							content: messages.errorDismiss,
							onClick: () => {
								dismissFlag(id);
							},
						},
						{
							content: messages.errorAction,
							onClick: () => {
								window.location.reload();
							},
						},
					],
				});
			},
			[dismissFlag, showFlag],
		),
	};
};

export default useFlags;
